import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import DashboardCard from '@/presentation/components/dashboard/Card'
import DashboardDriverTab from '@/presentation/components/dashboard/DriversTab'
import DashboardCommisionsTab from '@/presentation/components/dashboard/CommisionsTab'
import DashboardSignaturesTab from '@/presentation/components/dashboard/SignaturesTab'
import DashboardAnalyticsTab from '@/presentation/components/dashboard/AnalyticsTab'
import { useDashboard } from '@/presentation/hooks/useDashboard'
import { Period } from '@/core/shared/types/dashboard'
import {
  ErrorDisplay,
  InlineErrorDisplay,
} from '@/presentation/components/ui/ErrorDisplay'
import { DashboardLayout } from '@/presentation/layouts/DashboardLayout'
import DashboardCardSkeleton from '@/presentation/components/Skeletons/DashboardCardSkeleton'
import DriversTableSkeleton from '@/presentation/components/Skeletons/DriversTableSkeleton'
import AnalyticsSkeleton from '@/presentation/components/Skeletons/AnalyticsSkeleton'
import { getPeriodLabel } from '@/core/shared/utils/period'
import { useNavigateWithParams } from '@/presentation/hooks/useNavigateWithParams'

export default function SocioColaboradorDashboard() {
  const {
    summary,
    selectedPeriod,
    drivers,
    solicitations,
    isLoading,
    isLoadingRides,
    isLoadingDrivers,
    isLoadingSolicitations,
    handlePeriodChange,
    ridesError,
    driversError,
    solicitationsError,
    refreshDashboard,
    hasData,
  } = useDashboard()
  const { navigateWithParams } = useNavigateWithParams()

  const dashboardPeriod = selectedPeriod as unknown as Period
  const periodLabel = getPeriodLabel(selectedPeriod)

  const onPeriodChange = (period: Period, customDates?: { startDate: string; endDate: string }) => {
    handlePeriodChange(period as unknown as Period, customDates)
    navigateWithParams('/dashboard', { period })
  }

  const allRequestsFailed = ridesError && driversError && solicitationsError
  if (allRequestsFailed && !hasData) {
    return (
      <DashboardLayout
        selectedPeriod={dashboardPeriod}
        onPeriodChange={onPeriodChange}
        onRefresh={refreshDashboard}
        lastUpdatedAt={undefined}
        isRefreshing={isLoading}
      >
        <div className="flex items-center justify-center p-6 min-h-[60vh]">
          <ErrorDisplay
            title="Erro ao carregar o Dashboard"
            errorType="server"
            onRetry={refreshDashboard}
            isRetrying={isLoading}
          />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout
      selectedPeriod={dashboardPeriod}
      onPeriodChange={onPeriodChange}
      onRefresh={refreshDashboard}
      lastUpdatedAt={summary?.lastUpdatedAt || undefined}
      isRefreshing={isLoading}
    >
      <div className="p-6">
        {isLoadingRides ? (
          <DashboardCardSkeleton />
        ) : ridesError ? (
          <div className="mb-8">
            <InlineErrorDisplay message={ridesError.message} />
          </div>
        ) : summary ? (
          <DashboardCard summary={summary} />
        ) : null}

        <Tabs defaultValue="drivers" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 gap-2">
            <TabsTrigger value="drivers" className="truncate">
              Motoristas
            </TabsTrigger>
            <TabsTrigger value="Assignments" className="truncate">
              Assinaturas
            </TabsTrigger>
            <TabsTrigger value="analytics" className="truncate">
              Análises
            </TabsTrigger>
          </TabsList>

          <TabsContent value="drivers" className="space-y-6">
            {isLoadingDrivers ? (
              <DriversTableSkeleton />
            ) : driversError ? (
              <InlineErrorDisplay message={driversError.message} />
            ) : drivers ? (
              <DashboardDriverTab
                drivers={drivers}
                summary={summary}
                isLoadingStats={isLoadingRides}
              />
            ) : null}
          </TabsContent>

          <TabsContent value="commissions" className="space-y-6">
            <DashboardCommisionsTab />
          </TabsContent>

          <TabsContent value="Assignments" className="space-y-6">
            {isLoadingDrivers ? (
              <DriversTableSkeleton />
            ) : driversError ? (
              <InlineErrorDisplay message={driversError.message} />
            ) : drivers ? (
              <DashboardSignaturesTab drivers={drivers} />
            ) : null}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            {isLoadingRides || isLoadingSolicitations ? (
              <AnalyticsSkeleton />
            ) : ridesError || solicitationsError ? (
              <div className="space-y-3">
                {ridesError && (
                  <InlineErrorDisplay message={ridesError.message} />
                )}
                {solicitationsError && (
                  <InlineErrorDisplay message={solicitationsError.message} />
                )}
              </div>
            ) : summary ? (
              <DashboardAnalyticsTab
                summary={summary}
                solicitations={solicitations}
                isLoadingSolicitations={isLoadingSolicitations}
                solicitationsError={solicitationsError}
                periodLabel={periodLabel}
                driversForPeriod={(drivers || []).map((driver) => ({
                  name: driver.fullName,
                  ridesCompleted: driver.finishedRides,
                  commission: driver.rating,
                  ...driver,
                }))}
              />
            ) : null}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}
